import { Component, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Weight } from '@shared/model/weight.model';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { DropdownModel } from '@shared-features/models/dropdown-model';
import { Subject } from 'rxjs';
import { LookupService } from '@shared-features/services/lookups.service';
import { MeasurementUnit } from '@features/shipments/models/measurement-unit.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-weight-field',
  templateUrl: './weight-field.component.html',
  styleUrls: ['./weight-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: WeightFieldComponent,
    },
  ],
})
export class WeightFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  form: UntypedFormGroup;
  units: DropdownModel[] = [];

  private tempFormValue: Weight = null;
  private destroy$ = new Subject();
  private isDisabled = false;


  get value(): Weight {
    const value = this.form && this.form.value;
    return { weight: value?.weight, unit: value?.unit?.dropDownValue };
  }

  onTouched = () => {
  };
  onChange = (value: { weight: number; unit: string }) => {
  };

  constructor(private formBuilder: UntypedFormBuilder, private lookupService: LookupService, private translateService: TranslateService) {
  }

  ngOnInit(): void {
    this.initForm();
    this.lookupService
      .getMeasurementUnits()
      .pipe(take(1))
      .subscribe((data) => {
        this.units = data.map(
          (item: MeasurementUnit) =>
            ({
              dropDownDisplayName: this.translateService.instant('Shipments.' + item.code.toLowerCase()),
              dropDownValue: item.code,
            } as DropdownModel)
        );
        this.writeValue(this.tempFormValue || { weight: null, unit: this.units[0]?.dropDownValue } as any);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      weight: [''],
      unit: [''],
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe(({ weight, unit }) => {
      this.onChange(this.value);
      this.onTouched();
    });

    this.applyDisabledState();
  }


  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.applyDisabledState();
  }

  private applyDisabledState(): void {
    if (!this.form) {
      return;
    }

    if (this.isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  writeValue(value: Weight): void {
    if (this.tempFormValue) {
      this.tempFormValue = null;
    }

    const weight = (value && value.weight) || null;
    // Falls back to the unit already selected. The CVA writes `null` when it binds, and with the
    // units lookup served from `memoize` that write lands AFTER the default was seeded — blanking it.
    const unit = (value && value.unit) || this.form.get('unit')?.value?.dropDownValue || null;

    this.form.patchValue({
      weight,
      unit: this.units?.length ? this.getFromUnits(unit) : {
        dropdownDisplayName: unit || '',
        dropDownValue: unit
      },
    }, { onlySelf: true, emitEvent: false });

    if (!this.units?.length) {
      this.tempFormValue = value;
    }
  }

  getFromUnits(dropdownValue): any {
    return this.units.find((item) => item.dropDownValue === dropdownValue);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
